import { useState, useCallback, useEffect, useMemo, useRef } from 'react'

import { deleteAsset, postAsset } from '../api'
import { ALL_CHILDREN } from '../binder/hooks'
import { getHookPromiseErrorHandler } from '../common'

const batch = files => files.map(file => ({ file }))

const filesFromDataTransfer = dataTransfer => {
  const files = []
  if (dataTransfer.items) {
    // Ew DataTransferItemList doesn't support iterators.
    for (let i = 0; i < dataTransfer.items.length; i += 1) {
      if (dataTransfer.items[i].kind === 'file') {
        files.push(dataTransfer.items[i].getAsFile())
      }
    }
  } else {
    for (let i = 0; i < dataTransfer.files.length; i += 1) {
      files.push(dataTransfer.files[i])
    }
  }

  return files
}

/**
 * The useFileUpload hook performs, tracks, and reports back on file uploads.
 *
 * Among the values returned by the hook, the uploadLog list can use the most documentation:
 * it is a list of lists of objects. The lists of objects are the file batches as they were
 * received by the hook. These file batch lists contain objects, each representing a file
 * for upload. Finally, each object has the following properties:
 *
 * - file: The actual File object sent in for upload
 * - mediaId: The media ID associated with this file (if falsy, the file has not finished uploading yet)
 * - uploadedFile: The file that was uploaded in place of `file` (if uploadFile sent a different one)
 *
 * By default, each file is uploaded as a household asset. To upload it somewhere else, pass uploadFile, which
 * receives the File and must resolve to { id, file }: `id` becomes the entry’s mediaId and `file` (if given)
 * becomes its uploadedFile.
 *
 * By default, a failed upload sets error to the server’s or browser’s message. To choose the message instead,
 * pass getErrorMessage, which receives the error and returns the message.
 */
const useFileUpload = ({ household, clientId, onUpload, uploadFile, getErrorMessage }) => {
  const { id: householdId } = household ?? {}

  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [fileInProgress, setFileInProgress] = useState('')

  const [assetsToNames, setAssetsToNames] = useState({})
  const [media, setMedia] = useState([])
  const [uploadLog, setUploadLog] = useState([])
  const reportedBatches = useRef(new WeakSet())

  const [error, setError] = useState(null)
  const handleApiError = useMemo(() => getHookPromiseErrorHandler(setError), [])

  // A batch stops after the file in progress once the caller unmounts, so that leaving the page mid-batch neither
  // uses up uploads nobody will see nor updates state that is no longer rendered.
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
    }
  }, [])

  const isMounted = useCallback(() => mountedRef.current, [])

  const uploadHouseholdAsset = useCallback(
    file =>
      postAsset({
        householdId,
        childId: clientId === ALL_CHILDREN ? undefined : clientId,
        file
      }).then(id => ({ id })),
    [householdId, clientId]
  )

  const upload = uploadFile ?? uploadHouseholdAsset

  const uploadAssetQueue = useCallback(
    async (queue, checkActive) => {
      if (queue.length === 0) {
        setUploading(false)
        setFileInProgress('')
        return
      }

      const fileToUpload = queue[0]
      const { name } = fileToUpload

      setUploading(true)
      setFileInProgress(name)

      try {
        const { id: assetId, file: uploadedFile } = await upload(fileToUpload)

        if (!checkActive()) {
          return
        }

        setAssetsToNames(assetsToNames => ({
          ...assetsToNames,
          [assetId]: name
        }))

        setMedia(media => [...media, assetId])

        setUploadLog(uploadLog => {
          // This assumes that File objects can only ever appear once.
          const entryToUpdate = uploadLog.flat().find(entry => entry.file === fileToUpload)
          entryToUpdate.mediaId = assetId
          entryToUpdate.uploadedFile = uploadedFile

          return [...uploadLog] // Yes we are assuming a shallow copy.
        })

        uploadAssetQueue(queue.slice(1), checkActive)
      } catch (error) {
        if (!checkActive()) {
          return
        }
        setUploading(false)

        if (getErrorMessage) {
          setError(getErrorMessage(error))
        } else {
          handleApiError(error)
        }
      }
    },
    [handleApiError, upload, getErrorMessage]
  )

  // We report from an effect rather than the setUploadLog updater because React may run the updater twice. The
  // WeakSet, keyed on each batch’s array, keeps a batch from being reported more than once.
  useEffect(() => {
    if (!onUpload) {
      return
    }

    uploadLog
      .filter(batch => batch.length > 0 && batch.every(entry => Boolean(entry.mediaId)))
      .filter(batch => !reportedBatches.current.has(batch))
      .forEach(batch => {
        reportedBatches.current.add(batch)
        onUpload(batch)
      })
  }, [uploadLog, onUpload])

  const processDataTransfer = useCallback(
    (dataTransfer, checkActive) => {
      const files = filesFromDataTransfer(dataTransfer)
      if (dataTransfer.items) {
        dataTransfer.items.clear()
      } else {
        dataTransfer.clearData()
      }

      setUploadLog(uploadLog => [...uploadLog, batch(files)])
      uploadAssetQueue(files, checkActive)
    },
    [uploadAssetQueue]
  )

  const handleDrop = useCallback(
    event => {
      event.preventDefault()

      setDragging(false)
      processDataTransfer(event.dataTransfer, isMounted)
    },
    [processDataTransfer, isMounted]
  )

  const handleDragOver = event => {
    event.preventDefault()
    setDragging(true)
  }

  const handleDragLeave = event => setDragging(false)

  const uploadFiles = useCallback(
    files => {
      setUploadLog(uploadLog => [...uploadLog, batch(files)])
      uploadAssetQueue(files, isMounted)
    },
    [uploadAssetQueue, isMounted]
  )

  const handleAsset = useCallback(event => uploadFiles(Array.from(event.currentTarget.files)), [uploadFiles])

  const deleteAssetFunction = useCallback(
    assetId => {
      if (!media.includes(assetId)) {
        // Nothing to delete.
        return
      }

      setDeleting(true)
      setFileInProgress(assetsToNames[assetId] || assetId)

      try {
        deleteAsset(assetId).then(() => {
          // Avoid the mutator.
          const updatedMedia = media.slice()
          updatedMedia.splice(media.indexOf(assetId), 1)

          setDeleting(false)
          setFileInProgress('')
          setMedia(updatedMedia)
        })
      } catch (error) {
        handleApiError(error)
      }
    },
    [assetsToNames, handleApiError, media]
  )

  return {
    assetsToNames,
    deleting,
    dragging,
    error,
    setError,
    fileInProgress,
    media,
    uploadLog,
    uploading,
    createAsset: handleAsset,
    uploadFiles,
    deleteAsset: deleteAssetFunction,
    processDataTransfer,
    onDragDrop: handleDrop,
    onDragLeave: handleDragLeave,
    onDragOver: handleDragOver,
    setDragging,
    setMedia
  }
}

export { filesFromDataTransfer, useFileUpload }
